import React, { Suspense, lazy, useEffect, useRef } from "react";
import { Route, Routes, useLocation, Navigate } from "react-router-dom";
import {
  checkTokenExpiryAndLogout,
  initializeActivityTracking,
} from "./api/apiConnect";
import AOS from "aos";
import "aos/dist/aos.css";
import useBodyId from "./useBodyid";
import Header from "./components/header/header";
import Footer from "./components/footer/footer";
import ScrollToTop from "./components/scrollTop";
import PageNotFound from "./components/pageNotFound";
import LoadingBar from "react-top-loading-bar";
import useAuth from "./api/useAuth";
import ScrollTop from "./scrollTop";
import "./assets/css/wait.scss";
import SupportRequest from "./components/members/support_request";
import ThankYouPage from "./components/members/support_request_success";
// Lazy load components
const MainComponent = lazy(() => import("./Pages/Home/main_component"));
const Profile = lazy(() => import("./Pages/Profile/profile"));
const MemberLogin = lazy(() => import("./components/members/member_login"));
const RequestPassword = lazy(() =>
  import("./components/members/request-password")
);
const ContactUs = lazy(() => import("./components/members/contact-us"));
const CoxContactUs = lazy(() => import("./components/members/cox-contact-us"));
const TermsConditions = lazy(() =>
  import("./components/members/terms_conditions")
);
const PatentNotice = lazy(() => import("./components/members/patent_notice"));
const SampleReports = lazy(() =>
  import("./components/members/samplereports/sample_reports")
);
const Glossary = lazy(() =>
  import("./components/members/summaryreport/glossary")
);
const UploadLiftTool = lazy(() =>
  import("./components/members/upload_lifttool_file")
);
const CorrectionRequest = lazy(() =>
  import("./components/members/correction_request")
);
const EliteReports = lazy(() => import("./components/members/eliteReports"));
const NMVTISReports = lazy(() =>
  import("./components/members/nmvtisreport/nmvtis_history_report")
);
const SingleVINSearch = lazy(() =>
  import("./components/members/singleVINsearch")
);
const Forbidden = lazy(() => import("./components/forbidden"));
const CleanFullReport = lazy(() =>
  import("./components/members/fullreport/cleanfullreport")
);
const SalvageSampleReport = lazy(() =>
  import("./components/members/fullreport/salvagesamplereport")
);
const PasswordPromptPage = lazy(() =>
  import("./components/passwordPromptPage")
);
const NMVTISPasswordPromptPage = lazy(() =>
  import("./components/members/nmvtisreport/managePassword")
);
const BillCycleReport = lazy(() =>
  import("./components/members/billCycleReport")
);
const Dashboard = lazy(() =>
  import("./components/members/dashboard/dashboard")
);
const ReportUsageDashboard = lazy(() =>
  import("./components/members/reportUsageDashboard")
);
const Report = lazy(() => import("./components/members/fullreport/fullreport"));
const NMVTISReport = lazy(() =>
  import("./components/members/nmvtisreport/nmvtisReport")
);

const NMVTISHelp = lazy(() =>
  import("./components/members/nmvtisreport/nmvtis_help")
);
const ReportFromDashboard = lazy(() =>
  import("./components/members/fullreport/fullreport")
);
const SummaryReport = lazy(() =>
  import("./components/members/summaryreport/summaryreport")
);
const SampleSummaryReport = lazy(() =>
  import("./components/members/summaryreport/samplesummaryreport")
);
const SalvageSampleSummary = lazy(() =>
  import("./components/members/summaryreport/salvagesummarysample")
);
const CleanSampleSummary = lazy(() =>
  import("./components/members/summaryreport/cleansummarysample")
);
const ScanReport = lazy(() =>
  import("./components/members/scanreport/scanreport")
);

const Nmvtis = lazy(() =>
  import("./components/members/nmvtisreport/nmvtis_login")
);
const Nmvtislogout = lazy(() =>
  import("./components/members/nmvtisreport/nmvtis_logout")
);
const AccidentReport = lazy(() =>
  import("./components/members/accidentreport/accidentreport")
);
const Thankyou = lazy(() => import("./components/thankyou"));
const Unauthorized = lazy(() => import("./components/unauthorized"));
const ContactThankyou = lazy(() => import("./components/contactThankyou"));
const EmailThankyou = lazy(() => import("./components/emailThankyou"));
const RequestSubmitBox = lazy(() => import("./components/requestsubmit"));
const ScripterForm = lazy(() => import("./Pages/Scripter/ScripterForm"));
const MajorIssues = lazy(() => import("./components/members/majorissues"));
const Scripter = lazy(() => import("./components/members/scripter"));
const Terms = lazy(() => import("./components/members/terms"));
const BBPTerms = lazy(() => import("./components/members/bbpterms"));
const CpoBbpPlusTerms = lazy(() => import("./components/members/cpobbpplusterms"));
const Glossory = lazy(() => import("./components/members/glossory"));
function BeforeEachRoute() {
  const location = useLocation();
  const loadingBarRef = useRef(null);

  useEffect(() => {
    loadingBarRef.current.continuousStart();
    const timeoutId = setTimeout(() => {
      loadingBarRef.current.complete();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [location]);

  return <LoadingBar color="#004990" ref={loadingBarRef} />;
}

function App() {
  useBodyId();

  useEffect(() => {
    AOS.init({
      duration: 1000,
      easing: "ease-in-out",
      once: true,
    });
  }, []);

  // Suppress VideoJS deprecation warnings and Brightcove mixed content errors from Brightcove player iframe
  useEffect(() => {
    const originalWarn = console.warn;
    const originalError = console.error;

    console.warn = (...args) => {
      // Filter out VideoJS deprecation warnings
      const message = args[0]?.toString() || "";
      const fullMessage = args.join(" ");

      // Check for VideoJS warnings (case-insensitive)
      if (
        message.includes("VIDEOJS") ||
        message.includes("videojs") ||
        message.includes("VideoJS") ||
        message.includes("addRemoteTextTrack") ||
        message.includes("addremotetexttrack") ||
        message.includes("manualCleanup") ||
        message.includes("manualcleanup") ||
        fullMessage.includes("VIDEOJS") ||
        fullMessage.includes("addRemoteTextTrack") ||
        fullMessage.includes("manualCleanup")
      ) {
        // Suppress VideoJS warnings - they're from third-party Brightcove player
        // These warnings are from Brightcove's Video.js implementation and cannot be fixed
        // without modifying Brightcove's player code, which we don't control
        return;
      }
      // Call original warn for other messages
      originalWarn.apply(console, args);
    };

    console.error = (...args) => {
      // Filter out Brightcove mixed content errors and VideoJS errors
      const message = args[0]?.toString() || "";
      const fullMessage = args.join(" ");
      const errorObj = args[0];

      // Check if error object has boltdns URL (format: {url: 'http://manifest.prod.boltdns.net/...', statusCode: 0, ...})
      const hasBoltdnsUrl =
        errorObj &&
        typeof errorObj === "object" &&
        errorObj.url &&
        typeof errorObj.url === "string" &&
        errorObj.url.includes("boltdns");

      // Check for mixed content errors from Brightcove/Boltdns
      if (
        message.includes("Mixed Content") ||
        message.includes("mixed content") ||
        message.includes("manifest.prod.boltdns.net") ||
        message.includes("boltdns") ||
        message.includes("insecure XMLHttpRequest") ||
        message.includes("was loaded over HTTPS") ||
        message.includes("must be served over HTTPS") ||
        fullMessage.toLowerCase().includes("mixed content") ||
        fullMessage.toLowerCase().includes("boltdns") ||
        fullMessage.toLowerCase().includes("insecure xmlhttprequest") ||
        fullMessage.toLowerCase().includes("was loaded over https") ||
        hasBoltdnsUrl ||
        (errorObj &&
          typeof errorObj === "object" &&
          errorObj.statusCode === 0 &&
          hasBoltdnsUrl) ||
        JSON.stringify(args).toLowerCase().includes("boltdns")
      ) {
        // Suppress Brightcove mixed content errors - these are from Brightcove's player configuration
        // Brightcove needs to configure their player to use HTTPS for all resources
        return;
      }

      // Check for VideoJS errors (ProgressEvent, etc.)
      // Also check for error objects with statusCode 0 and boltdns URLs (VideoJS network errors)
      if (
        message.includes("VIDEOJS") ||
        message.includes("videojs") ||
        message.includes("VideoJS") ||
        message.includes("ProgressEvent") ||
        message.includes("Error: [object ProgressEvent]") ||
        fullMessage.toLowerCase().includes("videojs") ||
        fullMessage.toLowerCase().includes("progressevent") ||
        (errorObj &&
          typeof errorObj === "object" &&
          errorObj.message &&
          typeof errorObj.message === "string" &&
          errorObj.message.includes("ProgressEvent")) ||
        (errorObj &&
          typeof errorObj === "object" &&
          errorObj.statusCode === 0 &&
          hasBoltdnsUrl) ||
        (errorObj &&
          typeof errorObj === "object" &&
          errorObj.method === "GET" &&
          hasBoltdnsUrl)
      ) {
        // Suppress VideoJS errors from Brightcove player
        return;
      }

      // Check for VideoJS errors
      if (
        message.includes("VIDEOJS") ||
        message.includes("videojs") ||
        message.includes("VideoJS") ||
        message.includes("ProgressEvent") ||
        fullMessage.toLowerCase().includes("videojs") ||
        fullMessage.toLowerCase().includes("progressevent")
      ) {
        // Suppress VideoJS errors from Brightcove player
        return;
      }

      // Call original error for other messages
      originalError.apply(console, args);
    };

    return () => {
      // Restore original console methods on unmount
      console.warn = originalWarn;
      console.error = originalError;
    };
  }, []);

  useEffect(() => {
    // Initial token check
    checkTokenExpiryAndLogout();
    // Start tracking user activity for idle timeout
    initializeActivityTracking();
  }, []);

  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll(
      '[data-bs-toggle="tooltip"]'
    );
    [...tooltipTriggerList].forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);

  const location = useLocation();
  const hiddenRoutes = [
    "reportFromDashboard",
    "scripterform",
    "emailThankyou",
    "terms.do",
    "bbpterms.do",
    "glossory.do",
    "freelink_glossary.do",
  ];

  useEffect(() => {
    // id: 3 that should set Account Dashboard title
    const accountDashboardRoutes = [
      "/manageAccount",
      "/marketingResources",
      "/buyback",
      "/manageFreeLink",
      "/roiCalculator",
      "/serviceData",
      "/referFriend",
      "/faq",
    ];
    // id: 2 that should set Run Reports title
    const runReportsRoutes = [
      "/singleVINsearch",
      "/dashboard",
      "/reportUsageDashboard",
      "/sample-reports/glossary",
      "/eliteReports",
      "/nmvtisReports",
    ];
    if (location.pathname === "/" || location.pathname === "/gateway") {
      document.title = "Welcome to AutoCheck";
    } else if (
      location.pathname === "/contact-us" ||
      location.pathname === "/cox-contact-us"
    ) {
      document.title = "Contact Us";
    } else if (location.pathname === "/support-request") {
      document.title = "Support Request";
    } else if (location.pathname === "/upload-lifttool-file") {
      document.title = "AutoCheck Lift Tool Analysis Request Submission Form";
    } else if (location.pathname === "/correction-Request") {
      document.title = "Data Correction Request";
    } else if (accountDashboardRoutes.includes(location.pathname)) {
      document.title = "Account Dashboard";
    } else if (runReportsRoutes.includes(location.pathname)) {
      document.title = "AutoCheck Dashboard Run Reports";
    } else {
      document.title = "AutoCheck.com | Homepage | AutoCheck.com";
    }
  }, [location.pathname]);

  const shouldHideHeaderFooter = hiddenRoutes.some((route) =>
    location.pathname.includes(route)
  );

  return (
    <>
      {!shouldHideHeaderFooter && <Header />}
      {!shouldHideHeaderFooter && (
        <main>
          <div className="container-fluid p-0">
            <BeforeEachRoute />
            <Suspense fallback={<LoadingBar color="#004990" />}>
              <ScrollTop />
              <AppRoutes />
              <ScrollToTop />
            </Suspense>
          </div>
        </main>
      )}
      {shouldHideHeaderFooter && (
        <main>
          <div className="container-fluid p-0 mt-4">
            <BeforeEachRoute />
            <Suspense fallback={<LoadingBar color="#004990" />}>
              <ScrollTop />
              <AppRoutes />
              <ScrollToTop />
            </Suspense>
          </div>
        </main>
      )}
      {!shouldHideHeaderFooter && <Footer />}
    </>
  );
}
function hasRole(userRoles, requiredRoles) {
  if (!requiredRoles || requiredRoles.length === 0) return true;
  return requiredRoles.some((role) => userRoles.includes(role));
}

function GatewayRedirect() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const target = searchParams.get('target');
  
  // Handle target parameter redirects
  if (target === 'glossary') {
    return <Navigate to="/sample-reports/glossary" replace />;
  }
  
  // If there are other query parameters, render MainComponent
  if (searchParams.toString()) {
    return <MainComponent />;
  }
  
  // Otherwise, redirect to home
  return <Navigate to="/" replace />;
}

function AppRoutes() {
  const isAuthenticated = useAuth();
  const location = useLocation();
  const profile = JSON.parse(localStorage.getItem("tokenExtract"));
  const userRoles =
    profile && profile.roles
      ? profile.roles.split(",").map((item) => item.trim())
      : "";
  return (
    <Routes>
      <Route path="/" element={<MainComponent />} />
      <Route path="/scripter" element={<Scripter />} />
      <Route path="/members/scripter" element={<Scripter />} />
      <Route path="/gateway" element={<MainComponent />} />
      <Route
        path="/gateway.do"
        element={<GatewayRedirect />}
      />
      <Route path="/forbidden" element={<Forbidden />} />
      <Route
        path="/manageAccount"
        element={isAuthenticated ? <Profile /> : <MainComponent />}
      />
      <Route
        path="/marketingResources"
        element={isAuthenticated ? <Profile /> : <MainComponent />}
      />
      <Route
        path="/buyback"
        element={isAuthenticated ? <Profile /> : <MainComponent />}
      />
      <Route
        path="/manageFreeLink"
        element={
          isAuthenticated && hasRole(userRoles, ["MANAGE_FAST_LINK"]) ? (
            <Profile />
          ) : (
            <MainComponent />
          )
        }
      />
      <Route
        path="/roiCalculator"
        element={
          isAuthenticated && hasRole(userRoles, ["ROI_TOOL"]) ? (
            <Profile />
          ) : (
            <MainComponent />
          )
        }
      />
      <Route
        path="/serviceData"
        element={isAuthenticated ? <Profile /> : <MainComponent />}
      />
      <Route
        path="/referFriend"
        element={isAuthenticated ? <Profile /> : <MainComponent />}
      />
      <Route
        path="/faq"
        element={isAuthenticated ? <Profile /> : <MainComponent />}
      />

      <Route
        path="/login.do"
        element={
          isAuthenticated ? (
            profile?.member_type === 18 || profile?.member_type === "18" ? (
              <MainComponent />
            ) : (
              <SingleVINSearch />
            )
          ) : (
            <MemberLogin />
          )
        }
      />
      <Route
        path="/members/member-login"
        element={
          isAuthenticated ? (
            profile?.member_type === 18 || profile?.member_type === "18" ? (
              <MainComponent />
            ) : (
              <SingleVINSearch />
            )
          ) : (
            <MemberLogin />
          )
        }
      />
      <Route path="/request-password" element={<RequestPassword />} />
      {/* Redirect old URL to new kebab-case URL */}
      <Route
        path="/requestPassword"
        element={<Navigate to="/request-password" replace />}
      />

      <Route
        path="/contact-us"
        element={
          profile?.member_type === 19 ||
          profile?.member_type === "19" ||
          profile?.member_type === 18 ||
          profile?.member_type === "18" ? (
            <CoxContactUs />
          ) : (
            <ContactUs />
          )
        }
      />
      <Route path="/contact-thankyou" element={<ContactThankyou />} />
      <Route path="/terms-conditions" element={<TermsConditions />} />
      <Route path="/glossary" element={<Glossary />} />
      <Route path="/patent-notice" element={<PatentNotice />} />
      <Route
        path="/sample-reports/:reportType"
        element={<SampleReports key={location.key} />}
      />
      <Route path="/glossary" element={<Glossary />} />

      <Route
        path="/upload-lifttool-file"
        element={
          isAuthenticated && hasRole(userRoles, ["LIFT_TOOL"]) ? (
            <UploadLiftTool />
          ) : (
            <MainComponent />
          )
        }
      />
      <Route
        path="/support-request"
        element={
          isAuthenticated &&
          (hasRole(userRoles, ["COX_SUPPORT"]) ||
            profile?.member_type === 18 ||
            profile?.member_type === "18") ? (
            <SupportRequest />
          ) : (
            <MainComponent />
          )
        }
      />
      <Route
        path="/correction-request"
        element={
          isAuthenticated &&
          hasRole(userRoles, ["ALL"]) &&
          !hasRole(userRoles, ["COX_SUPPORT"]) ? (
            <CorrectionRequest />
          ) : (
            <MainComponent />
          )
        }
      />

      <Route
        path="/eliteReports"
        element={
          isAuthenticated && hasRole(userRoles, ["ELITE_REPORTS"]) ? (
            <EliteReports />
          ) : (
            <MainComponent />
          )
        }
      />
      <Route
        path="/nmvtisReports"
        element={
          isAuthenticated && hasRole(userRoles, ["NMVTIS_USER"]) ? (
            <NMVTISReports />
          ) : (
            <MainComponent />
          )
        }
      />
      <Route
        path="/singleVINsearch"
        element={
          isAuthenticated ? (
            profile?.member_type === 18 || profile?.member_type === "18" ? (
              <MainComponent />
            ) : (
              <SingleVINSearch />
            )
          ) : (
            <MainComponent />
          )
        }
      />
      <Route
        path="/dashboard"
        element={isAuthenticated ? <Dashboard /> : <MainComponent />}
      />
      <Route
        path="/reportUsageDashboard"
        element={
          isAuthenticated && hasRole(userRoles, ["NINETY_DAY_USAGE"]) ? (
            <ReportUsageDashboard />
          ) : (
            <MainComponent />
          )
        }
      />
      <Route
        path="/report/:param1"
        element={
          isAuthenticated ? <Report key={location.key} /> : <MainComponent />
        }
      />
      <Route
        path="/nmvtisreport/:param1"
        element={
          isAuthenticated && hasRole(userRoles, ["NMVTIS_USER"]) ? (
            <NMVTISReport key={location.key} />
          ) : (
            <MainComponent />
          )
        }
      />
      <Route
        path="/reportFromDashboard/:param1"
        element={isAuthenticated ? <ReportFromDashboard /> : <MainComponent />}
      />
      <Route
        path="/summaryreport"
        element={isAuthenticated ? <SummaryReport /> : <MainComponent />}
      />
      <Route
        path="/scanreport"
        element={isAuthenticated ? <ScanReport /> : <MainComponent />}
      />
      <Route
        path="/accidentreport"
        element={isAuthenticated ? <AccidentReport /> : <MainComponent />}
      />
      <Route
        path="/majorissues"
        element={isAuthenticated ? <MajorIssues /> : <MainComponent />}
      />
      <Route
        path="/thankyou"
        element={isAuthenticated ? <Thankyou /> : <MainComponent />}
      />
      <Route
        path="/unauthorized"
        element={isAuthenticated ? <Unauthorized /> : <MainComponent />}
      />
      <Route path="/contact-thankyou" element={<ContactThankyou />} />
      <Route path="/emailThankyou" element={<EmailThankyou />} />
      <Route
        path="/requestsubmitted"
        element={isAuthenticated ? <RequestSubmitBox /> : <MainComponent />}
      />
      <Route
        path="/billcycle"
        element={isAuthenticated ? <BillCycleReport /> : <MainComponent />}
      />
      <Route
        path="/passwordChangePrompt"
        element={isAuthenticated ? <PasswordPromptPage /> : <MainComponent />}
      />
      <Route
        path="/managePassword"
        element={
          isAuthenticated ? <NMVTISPasswordPromptPage /> : <MainComponent />
        }
      />
      <Route path="/fullClean" element={<CleanFullReport />} />
      <Route path="/fullSalvage" element={<SalvageSampleReport />} />
      <Route path="/summaryClean" element={<CleanSampleSummary />} />
      <Route path="/summarySalvage" element={<SalvageSampleSummary />} />
      <Route path="/nmvtis.do" element={<Nmvtis />} />
      <Route path="/nmvtishelp" element={<NMVTISHelp />} />
      <Route path="/nmvtisLogout" element={<Nmvtislogout />} />
      <Route path="/scripterform" element={<ScripterForm />} />
      <Route path="/thank-you" element={<ThankYouPage />} />
      <Route path="/terms.do" element={<Terms />} />
      <Route path="/bbpterms.do" element={<BBPTerms />} />
      <Route path="/cpobbpplusterms.do" element={<CpoBbpPlusTerms />} />
      <Route path="/freelink_glossary.do" element={<Glossory />} />
      {/* Catch-all route - must be last */}
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
}

export default App;
